import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, User } from "lucide-react";
import { RevealOnScroll, FadeUp } from "../../lib/motion";
import { useLang } from "../../i18n/LanguageContext";

// ===== PORTEFEUILLE — Ajoutez vos joueurs ici (disposition Sport Cover) =====
// { name, flags, club, clubLogo, photo, transfermarkt, filter }
// filter : "gardiens" | "defenseurs" | "milieux" | "attaquants" | "coachs"
// flags : tableau de codes ISO pays (ex ["ma","fr"]) — affichés en pastilles rondes
// clubLogo & photo : URL image | transfermarkt : lien du profil
const PLAYERS = [
  {
    name: "Hamza Sakhi",
    flags: ["ma", "fr"],
    club: "Meizhou Hakka",
    clubLogo: "/clubs/meizhou-hakka.png",
    photo: "/joueurs/hamza-sakhi.jpg",
    transfermarkt: "https://www.transfermarkt.fr/hamza-sakhi/profil/spieler/263951",
    filter: "milieux",
  },
];

const FILTER_KEYS = ["all", "gardiens", "defenseurs", "milieux", "attaquants", "coachs"];

const FlagPill = ({ code }) => (
  <span className="inline-block h-7 w-7 rounded-full overflow-hidden ring-2 ring-white/80 shadow-md bg-neutral-800">
    <img
      src={`https://flagcdn.com/w80/${code}.png`}
      alt={code.toUpperCase()}
      className="h-full w-full object-cover"
      loading="lazy"
    />
  </span>
);

const PlayerCard = ({ p, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className="group relative aspect-[3/4] [perspective:1200px]"
    data-testid={`player-card-${index}`}
  >
    <div
      className="relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]"
    >
      {/* FRONT */}
      <div className="absolute inset-0 border border-white/15 bg-neutral-950 overflow-hidden [backface-visibility:hidden]">
        <img
          src={p.photo}
          alt={p.name}
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
        {p.flags && p.flags.length > 0 && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            {p.flags.map((c) => (
              <FlagPill key={c} code={c} />
            ))}
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="font-display font-bold uppercase text-lg md:text-xl tracking-tight text-white leading-tight">
            {p.name}
          </div>
          <div className="mt-2 flex items-center gap-2">
            {p.clubLogo && (
              <img src={p.clubLogo} alt={p.club} className="h-5 w-5 object-contain" />
            )}
            <span className="text-sm text-white/80">{p.club}</span>
          </div>
        </div>
      </div>

      {/* BACK */}
      <div className="absolute inset-0 border border-white/20 bg-neutral-950 overflow-hidden [backface-visibility:hidden] [transform:rotateY(180deg)]">
        <div className="absolute inset-0 opacity-30 grayscale">
          <img src={p.photo} alt="" className="h-full w-full object-cover object-top blur-sm" />
        </div>
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 h-full w-full flex flex-col justify-between p-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">
              Profil
            </span>
            <div className="mt-2 font-display font-bold uppercase text-lg md:text-xl tracking-tight text-white leading-tight">
              {p.name}
            </div>
            <div className="mt-1 flex items-center gap-2">
              {p.flags && p.flags.map((c) => <FlagPill key={c} code={c} />)}
              <span className="text-xs text-white/60 ml-1">{p.club}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {p.transfermarkt && (
              <a
                href={p.transfermarkt}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`player-link-${index}`}
                className="group/btn inline-flex items-center justify-between gap-2 px-4 py-3 border border-white/40 text-white text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-white hover:text-black transition-colors duration-300"
              >
                <span>Transfermarkt</span>
                <ArrowUpRight size={16} className="shrink-0" />
              </a>
            )}
            <a
              href="https://hmsportconsulting.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`player-hm-link-${index}`}
              className="inline-flex items-center justify-between gap-2 px-4 py-3 bg-white text-black text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-white/90 transition-colors duration-300"
            >
              <span>HM Sport Consulting</span>
              <ArrowUpRight size={16} className="shrink-0" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

const PlaceholderCard = ({ index, name }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className="group relative aspect-[3/4] border border-white/15 bg-neutral-950 overflow-hidden"
    data-testid={`player-placeholder-${index}`}
  >
    <div className="absolute inset-0 flex items-center justify-center text-white/15 group-hover:text-white/30 transition-colors duration-500">
      <User size={54} strokeWidth={1} />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <div className="font-display font-bold uppercase text-base tracking-tight text-white/60 leading-tight">
        {name}
      </div>
    </div>
  </motion.div>
);

export const Roster = () => {
  const { t } = useLang();
  const r = t.roster;
  const [active, setActive] = useState(0);

  const activeKey = FILTER_KEYS[active];
  const filtered = active === 0 ? PLAYERS : PLAYERS.filter((p) => p.filter === activeKey);
  const isEmpty = filtered.length === 0;

  return (
    <section id="joueurs" className="bg-black text-white" data-testid="roster-section">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16 gap-6">
          <div>
            <FadeUp>
              <span className="text-sm md:text-base uppercase tracking-[0.25em] font-bold text-white/50">
                {r.label}
              </span>
            </FadeUp>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight mt-6">
              {r.titleLines.map((line, i) => (
                <RevealOnScroll key={i} delay={i * 0.1}>
                  {line}
                </RevealOnScroll>
              ))}
            </h2>
          </div>
          <FadeUp delay={0.2} className="max-w-sm text-white/60 text-lg leading-relaxed">
            {r.subtitle}
          </FadeUp>
        </div>

        <FadeUp>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-12 border-b border-white/15 pb-6">
            {r.filters.map((f, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`roster-filter-${i}`}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-bold border transition-colors duration-300 ${
                  active === i
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-white/60 border-white/25 hover:text-white hover:border-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {isEmpty
            ? Array.from({ length: 4 }).map((_, i) => (
                <PlaceholderCard key={i} index={i} name={r.emptyName} />
              ))
            : filtered.map((p, i) => <PlayerCard key={i} p={p} index={i} />)}
        </div>

        <FadeUp delay={0.15} className="mt-14">
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="roster-cta"
            className="inline-flex items-center gap-3 px-8 py-4 border border-white text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-white hover:text-black"
          >
            {r.cta}
          </a>
        </FadeUp>
      </div>
    </section>
  );
};

export default Roster;
